import React, { useEffect, useState, useContext, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { MCard, MSpinner } from "components/Shared";
import { DATA_TYPE_PERCENT_WHOLE_NUMBER, DATA_TYPE_MONEY } from "typeConstants/dataTypes";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import { getCaseInfo, updateQAReduxCaseInfo } from "actions/qualityAssurance";
import responseHandler from "responseHandler";
import { updateCaseHeaderDep } from "requests/homeowner/caseforms";
import { userHasRight } from "selectors/rights";
import { CALC_HEADER_SETTINGS_EDIT } from "typeConstants/rights";
import { updateCalcHeaderSettings } from "requests/qualityAssurance/index.ts";

const Coverage = props => {
  const [updatingCaseHeader, setUpdatingCaseHeader] = useState(false);
  const dialog = useContext(DialogContext);
  const mounted = useRef(true);

  useEffect(() => {
  return () => mounted.current = false;
  }, []);
  
  useEffect(() => {
    if (props.rctNeedsCommit || props.requestingCaseRCT) props.getCaseInfo(props.controlNumber);
    return () => { };
  }, [props.rctNeedsCommit, props.requestingCaseRCT]);
  
  const saveCoverageChanges = (_, editedObj) => {
    if (Object.keys(editedObj).length) {
      setUpdatingCaseHeader(true);
      // Calc header settings are updated using a different end point
      if (Object.keys(editedObj).includes("depreciationMultiplier")) {
        let settings = { DepreciationMultiplier: editedObj["depreciationMultiplier"] };
        let callback = () => setUpdatingCaseHeader(false);
        updateCalcHeaderSettings(props.controlNumber, settings, Object.keys(editedObj).length > 1 ? null : callback, Object.keys(editedObj).length > 1 ? null : callback, mounted);
      }
      if (Object.keys(editedObj).includes("coverageAOut")) {
        if ((editedObj.coverageAOut && editedObj.coverageAOut > 0) && (props.qaCase.caseInfo.coverageAIn && props.qaCase.caseInfo.coverageAIn > 0)) {
          let { qaQueueRules } = props.policyInfo;
          let newPercentITV = (props.qaCase.caseInfo.coverageAIn / editedObj.coverageAOut) * 100;
          if (newPercentITV < (qaQueueRules.qaRuleItvMgrReviewMin ?? newPercentITV)
            || newPercentITV > (qaQueueRules.qaRuleItvMgrReviewMax ?? newPercentITV)) {
            dialog.showOk("Replacement Cost", "You MUST consult with a manager prior to completing this report.");
          } else if (newPercentITV < (qaQueueRules.qaRuleItvWarningMin ?? newPercentITV)
            || newPercentITV > (qaQueueRules.qaRuleItvWarningMax ?? newPercentITV)) {
            dialog.showOk("Replacement Cost", "Please check all data entry points within the RC for accuracy.");
          }
        }
        updateCaseHeaderDep(props.controlNumber, editedObj, () => setUpdatingCaseHeader(false), mounted);
      }
      props.updateQAReduxCaseInfo(editedObj);
    } else props.makeToastWarning("Enter Coverage A Out or Depreciation amount to save your changes.");
  };  

  const getFields = () => {
    let { coverageAIn, coverageAOut, depreciationMultiplier } = props.qaCase?.caseInfo;
    const fields = [{
      key: "coverageAIn",
      label: "Coverage A In",
      value: coverageAIn,
      editable: false,
      dataType: DATA_TYPE_MONEY,
      dataTypeOptions: {
        numDigitsAfterDecimal: 0
      }
    }, {
      key: "coverageAOut",
      label: "Coverage A Out",
      value: coverageAOut,
      editable: true,
      dataType: DATA_TYPE_MONEY,
      dataTypeOptions: {
        numDigitsAfterDecimal: 0
      } 
    }, {
      key: "percent-to-value",
      label: "% to Value",
      value: !coverageAIn || !coverageAOut ? 0 : (coverageAIn / coverageAOut) * 100,
      editable: false,
      dataType: DATA_TYPE_PERCENT_WHOLE_NUMBER,
    }];
    if (props.qaCase?.editDepreciationEnabled && props.canEditDepreciation) {
      fields.push({
        key: "depreciationMultiplier",
        label: "Depreciation %",
        value: depreciationMultiplier,
        editable: true,
        dataType: DATA_TYPE_PERCENT_WHOLE_NUMBER,
      });
    }
    return fields;
  };

  if (updatingCaseHeader) return (<MSpinner />);

  return (
    <MCard 
      title={"Coverage"}
      fields={getFields()}
      isEditable={true}
      isFetching={updatingCaseHeader}
      onEditFinished={saveCoverageChanges}
    />
  );
};

Coverage.propTypes = {
  controlNumber: PropTypes.number,
  // Redux State
  qaCase: PropTypes.object,
  policyInfo: PropTypes.object,
  rctNeedsCommit: PropTypes.bool,
  requestingCaseRCT: PropTypes.bool,
  canEditDepreciation: PropTypes.bool,
  // Redux Dispatch
  makeToastWarning: PropTypes.func,
  getCaseInfo: PropTypes.func,
  updateQAReduxCaseInfo: PropTypes.func,
};

const mapStateToProps = state => {
  return {
    qaCase: state.qualityAssurance.qaCase,
    policyInfo: state.caseForms.caseHeader.policyInfo,
    rctNeedsCommit: state.qualityAssurance.qaVariables.phoenixRctNeedsCommit,
    requestingCaseRCT: state.qualityAssurance.requestingCaseRCT,
    canEditDepreciation: userHasRight(CALC_HEADER_SETTINGS_EDIT)(state),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    getCaseInfo(controlNumber) {
      dispatch(getCaseInfo(controlNumber));
    },
    makeToastWarning(text) {
      responseHandler.customToast(text, "warning", dispatch);
    },
    updateQAReduxCaseInfo(editedObj) {
      dispatch(updateQAReduxCaseInfo(editedObj));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Coverage);